'use client';

import { Button } from '@common/components';
import { SvgArrowRight, SvgChevronleft, SvgChevronright } from '@common/icon';
import { cn } from '@core/utils';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

import { Image } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useGlobalStore } from '@/stores/GlobalStore';

const SLIDE_IMAGE_BASE = 'https://cdn.welfarehello.com/static/home';

const REWARD_SLIDES = [
  { src: `${SLIDE_IMAGE_BASE}/reward-1.png`, alt: '이달의 혜택 메인 화면' },
  { src: `${SLIDE_IMAGE_BASE}/reward-2.png`, alt: '미션 추천 화면' },
  { src: `${SLIDE_IMAGE_BASE}/reward-3.png`, alt: '리워드 충전소 홈 화면' },
  { src: `${SLIDE_IMAGE_BASE}/reward-4.png`, alt: '전체 미션 화면' },
];

const REWARD_BENEFITS = [
  { lead: '', highlight: '현금성 웰포인트', tail: ' 적립' },
  {
    lead: '',
    highlight: '네이버페이 포인트',
    tail: ' 등 모바일 쿠폰 교환 가능',
  },
  { lead: '매일 갱신되는 미션으로 ', highlight: '꾸준한 적립', tail: '' },
];

export const AppInstallConfirmModal = () => {
  const router = useCustomRouter();
  const [isShowAppInstallConfirmModal, setIsShowAppInstallConfirmModal] =
    useGlobalStore((state) => [
      state.isShowAppInstallConfirmModal,
      state.setIsShowAppInstallConfirmModal,
    ]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;

    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const handleClose = () => setIsShowAppInstallConfirmModal(false);

  const handleClickInstall = () => {
    setIsShowAppInstallConfirmModal(false);
    router.push(ROUTES.APP_DOWNLOAD.pathname);
  };

  return (
    <ResponsiveDialog
      opened={isShowAppInstallConfirmModal}
      onClose={handleClose}
    >
      <div className="flex flex-col gap-12">
        <header className="flex flex-col items-center gap-8">
          <span className="inline-flex items-center gap-4 rounded-pill bg-B-50 px-8 py-4 font-700 text-B-500 typo-caption2">
            📱 모바일 앱 전용 혜택
          </span>
          <h2 className="break-keep text-center font-800 text-Gray-900 typo-h6">
            매일 미션 참여하고{' '}
            <b className="font-800 text-B-500">현금처럼 쓰는 포인트</b> 받기
          </h2>
          <p className="whitespace-pre-line break-keep text-center font-400 text-Gray-500 typo-caption1">
            웰로 앱에서만 만나는 리워드 충전소,{'\n'}지금 어떤 혜택이 있는지
            살펴보세요.
          </p>
        </header>

        <div className="flex flex-col items-center gap-8">
          <div className="relative w-full">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {REWARD_SLIDES.map((slide) => (
                  <div
                    key={slide.src}
                    className="flex min-w-0 flex-[0_0_100%] justify-center"
                  >
                    <div className="relative aspect-[3/4] w-180 overflow-hidden rounded-large border border-Gray-100 [-webkit-mask-image:linear-gradient(to_bottom,black_88%,transparent)] [mask-image:linear-gradient(to_bottom,black_88%,transparent)]">
                      <Image
                        alt={slide.alt}
                        className="object-cover object-top"
                        fill
                        sizes="180px"
                        src={slide.src}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              aria-label="이전 혜택"
              className="absolute left-0 top-1/2 flex size-32 -translate-y-1/2 items-center justify-center rounded-pill border border-Gray-100 bg-Gray-white text-Gray-600 shadow-base"
              onClick={() => emblaApi?.scrollPrev()}
              type="button"
            >
              <SvgChevronleft className="size-16" />
            </button>
            <button
              aria-label="다음 혜택"
              className="absolute right-0 top-1/2 flex size-32 -translate-y-1/2 items-center justify-center rounded-pill border border-Gray-100 bg-Gray-white text-Gray-600 shadow-base"
              onClick={() => emblaApi?.scrollNext()}
              type="button"
            >
              <SvgChevronright className="size-16" />
            </button>
          </div>

          <div className="flex items-center gap-4">
            {REWARD_SLIDES.map((slide, index) => (
              <button
                key={slide.src}
                aria-current={index === selectedIndex}
                aria-label={`${index + 1}번 혜택`}
                className={cn(
                  'h-4 rounded-pill transition-all duration-200',
                  index === selectedIndex ? 'w-16 bg-B-500' : 'w-4 bg-Gray-300',
                )}
                onClick={() => emblaApi?.scrollTo(index)}
                type="button"
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-small bg-B-25 px-16 py-12 text-left">
          <ul className="flex flex-col gap-8">
            {REWARD_BENEFITS.map(({ lead, highlight, tail }) => (
              <li
                key={highlight}
                className="flex items-start gap-8 break-keep font-400 text-Gray-800 typo-caption1"
              >
                <span className="mt-6 size-4 shrink-0 rounded-pill bg-B-500" />
                <span>
                  {lead}
                  <b className="font-700 text-B-600">{highlight}</b>
                  {tail}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <Button
          dimensions="H48"
          onClick={handleClickInstall}
          theme="secondary-fill"
        >
          앱 설치하고 혜택 받기
          <SvgArrowRight className="size-20" />
        </Button>

        <button
          className="font-500 text-Gray-400 underline underline-offset-2 typo-caption2"
          onClick={handleClose}
          type="button"
        >
          다음에 볼게요
        </button>
      </div>
    </ResponsiveDialog>
  );
};
